import React, {useEffect, useRef, useState} from 'react'
import {createRoot} from 'react-dom/client'
import HTMLFlipBook from 'react-pageflip'
import {Container} from 'react-bootstrap'
import {toast} from 'react-toastify'

import {pdf} from '@react-pdf/renderer'
import {saveAs} from 'file-saver'

import bgImage from '../../assets/img/bg.png'

import Api from '../../../thesis-and-dissertations/helpers/api'
import Page1 from './Page1'
import Page2 from './Page2'
import {useNavigate, useParams} from 'react-router-dom'
import Page3 from './Page3'
import Page4 from './Page4'
import './style.css'
import PdfPage from './PdfPage'

const HTMLFlipBookAny = HTMLFlipBook as any
const api = new Api()

// Komponen halaman flipbook
interface PageProps {
  children: React.ReactNode
}

const FlipbookPage = React.forwardRef<HTMLDivElement, PageProps>(({children}, ref) => {
  return (
    <div
      className='page'
      ref={ref}
      style={{
        width: '100%',
        height: '100%',
        backgroundImage: `url(${bgImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        padding: '20px',
      }}
    >
      {children}
    </div>
  )
})

FlipbookPage.displayName = 'FlipbookPage'

// Flipbook utama
export default function FlipbookContent() {
  const contentRef = useRef<HTMLDivElement>(null)
  let navigate = useNavigate()
  const {id} = useParams()

  const bookRef = useRef<any>(null)
  const [current, setCurrent] = useState<number>(1) // 1-based
  const [total, setTotal] = useState<number>(0)
  const [data, setData] = useState<any>({})
  const [loading, setLoading] = useState<boolean>(false)
  const [dimensions, setDimensions] = useState({
    width: 500,
    height: 710,
  })
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)

  const showCover = false
  const lastPage = showCover ? total : total - 1
  const isFirst = current === 1
  const isLast = current >= lastPage

  useEffect(() => {
    getProfileById(id ?? '')
    updateDimensions()
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const handleResize = () => {
    updateDimensions()
    setIsMobile(window.innerWidth < 768)
  }

  const updateDimensions = () => {
    // Untuk desktop, pertahankan ukuran asli
    if (window.innerWidth >= 992) {
      setDimensions({width: 500, height: 710})
      return
    }

    // Untuk tablet
    if (window.innerWidth >= 768) {
      const width = Math.min(450, window.innerWidth - 100)
      const height = width * 1.42 // Rasio aspek 710/500 = 1.42
      setDimensions({width, height})
      return
    }

    // Untuk mobile
    const width = Math.min(350, window.innerWidth - 40)
    const height = width * 1.42
    setDimensions({width, height})
  }

  // Ambil total halaman setelah flipbook selesai inisialisasi
  const handleInit = () => {
    const flip = bookRef.current?.pageFlip?.()
    if (flip) {
      setTotal(flip.getPageCount())
      // sinkronkan current awal (kalau default bukan 1)
      setCurrent(flip.getCurrentPageIndex() + 1)
    }
  }

  // Update current saat halaman dibalik
  const handleFlip = (e: any) => {
    // e.data adalah index 0-based
    setCurrent(e.data + 1)
  }

  const goPrev = () => {
    bookRef.current?.pageFlip()?.flipPrev()
  }

  const goNext = () => {
    bookRef.current?.pageFlip()?.flipNext()
  }

  const handleShare = () => {
    if (navigator.share) {
      navigator
        .share({
          title: 'Flipbook Saya',
          text: `Lihat halaman ke-${current} dari flipbook ini.`,
          url: window.location.href + `?page=${current}`, // bisa kasih query param
        })
        .catch((err) => console.error('Gagal share', err))
      return
    }

    // fallback kalau browser tidak support
    alert('Browser kamu tidak mendukung Web Share API')
  }

  const handleClose = () => {
    if (window.history.state && window.history.state.idx > 0) {
      // ada history di React Router
      navigate(-1)
      return
    }

    // langsung buka pertama kali, fallback ke home
    navigate('/')
  }

  const getProfileById = (id: string) => {
    setLoading(true)
    api
      .getDetailProfilePublic(id)
      .then((response: any) => {
        setLoading(false)
        const {data} = response.data
        setData(data)
        toast.success(data?.meta?.message ?? data?.message)
      })
      .catch((error: any) => {
        setLoading(false)
        const {data} = error.response
        toast.error(data?.meta?.message)
      })
  }

  const downloadPDF = async () => {
    try {
      // Create a new PDF document
      const doc = <PdfPage data={data} />

      // Generate PDF blob
      const pdfBlob = await pdf(doc).toBlob()

      // Download the PDF
      saveAs(pdfBlob, `LPDP-Alumni-${data?.nama_lengkap || 'Profile'}.pdf`)

      toast.success('PDF berhasil diunduh')
    } catch (error) {
      console.error('Error generating PDF:', error)
      toast.error('Gagal mengunduh PDF')
    }
  }

  return (
    <Container
      fluid
      className='flipbook-container d-flex flex-column justify-content-center align-items-center vh-100'
      style={{background: '#000000D9', padding: '20px 0'}}
    >
      {/* Wrapper untuk flipbook + tombol */}
      <div
        ref={contentRef}
        className='position-relative flipbook-wrapper'
        style={{background: '#F5F5EF'}}
      >
        {/* Tombol kiri - Desktop */}
        <div
          className='flip-btn position-absolute top-50 translate-middle-y d-none d-md-flex align-items-center justify-content-center'
          style={{
            left: '-50px',
            width: '40px',
            height: '40px',
            cursor: isFirst ? 'not-allowed' : 'pointer',
            zIndex: 1000,
            background: isFirst ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.5)',
            borderRadius: '50%',
          }}
          onClick={isFirst ? undefined : goPrev}
        >
          <i className={`fa fa-angle-left ${isFirst ? 'text-muted' : 'text-white'}`}></i>
        </div>

        <HTMLFlipBookAny
          ref={bookRef}
          width={dimensions.width}
          height={dimensions.height}
          size='fixed'
          showCover={showCover}
          mobileScrollSupport={true}
          usePortrait={false}
          onInit={handleInit}
          onFlip={handleFlip}
          className='flipbook'
          style={{maxWidth: '100%'}}
        >
          <FlipbookPage>
            <Page1 data={data} isMobile={isMobile} />
          </FlipbookPage>
          <FlipbookPage>
            <Page2 data={data} isMobile={isMobile} />
          </FlipbookPage>
          <FlipbookPage>
            <Page3 data={data} isMobile={isMobile} />
          </FlipbookPage>
          <FlipbookPage>
            <Page4 data={data} isMobile={isMobile} />
          </FlipbookPage>
        </HTMLFlipBookAny>

        {/* Tombol kanan (X, Next, Share) - Desktop */}
        <div
          className='position-absolute top-0 h-100 d-none d-md-flex flex-column justify-content-between align-items-center text-white'
          style={{
            right: '-50px',
            padding: '20px 0',
            width: '40px',
            zIndex: 1000,
          }}
        >
          {/* Tombol X (atas) */}
          <div
            className='flip-btn d-flex align-items-center justify-content-center'
            style={{
              cursor: 'pointer',
              width: '40px',
              height: '40px',
              background: 'rgba(0,0,0,0.5)',
              borderRadius: '50%',
            }}
            onClick={handleClose}
          >
            <i className='fa fa-close text-white'></i>
          </div>

          {/* Tombol Next (tengah) */}
          <div
            className='flip-btn d-flex align-items-center justify-content-center'
            style={{
              cursor: isLast ? 'not-allowed' : 'pointer',
              width: '40px',
              height: '40px',
              background: isLast ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.5)',
              borderRadius: '50%',
            }}
            onClick={isLast ? undefined : goNext}
          >
            <i className={`fa fa-angle-right ${isLast ? 'text-muted' : 'text-white'}`}></i>
          </div>

          {/* Tombol Share (bawah) */}
          <div className='d-flex flex-column align-items-center'>
            <div
              className='flip-btn d-flex align-items-center justify-content-center mb-2'
              style={{
                cursor: 'pointer',
                width: '40px',
                height: '40px',
                background: 'rgba(0,0,0,0.5)',
                borderRadius: '50%',
              }}
              onClick={downloadPDF}
            >
              <i className='bi bi-download text-white'></i>
            </div>
            <div
              className='flip-btn d-flex align-items-center justify-content-center'
              style={{
                cursor: 'pointer',
                width: '40px',
                height: '40px',
                background: 'rgba(0,0,0,0.5)',
                borderRadius: '50%',
              }}
              onClick={handleShare}
            >
              <i className='bi bi-share text-white'></i>
            </div>
          </div>
        </div>

        {/* Tombol navigasi mobile - hanya muncul di layar kecil */}
        <div
          className='d-md-none mobile-nav-buttons position-absolute w-100 d-flex justify-content-between align-items-center px-3'
          style={{
            bottom: '-60px',
            zIndex: 1000,
          }}
        >
          <div
            className='d-flex align-items-center justify-content-center'
            style={{
              cursor: isFirst ? 'not-allowed' : 'pointer',
              padding: '10px',
              width: '50px',
              height: '50px',
              background: isFirst ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.7)',
              borderRadius: '50%',
            }}
            onClick={isFirst ? undefined : goPrev}
          >
            <i className={`fa fa-chevron-left ${isFirst ? 'text-muted' : 'text-white'}`}></i>
          </div>

          <div className='d-flex'>
            <div
              className='d-flex align-items-center justify-content-center me-2'
              style={{
                cursor: 'pointer',
                padding: '10px',
                width: '50px',
                height: '50px',
                background: 'rgba(0,0,0,0.7)',
                borderRadius: '50%',
              }}
              onClick={handleClose}
            >
              <i className='fa fa-times text-white'></i>
            </div>
            <div
              className='d-flex align-items-center justify-content-center me-2'
              style={{
                cursor: 'pointer',
                padding: '10px',
                width: '50px',
                height: '50px',
                background: 'rgba(0,0,0,0.7)',
                borderRadius: '50%',
              }}
              onClick={downloadPDF}
            >
              <i className='bi bi-download text-white'></i>
            </div>
            <div
              className='d-flex align-items-center justify-content-center'
              style={{
                cursor: 'pointer',
                padding: '10px',
                width: '50px',
                height: '50px',
                background: 'rgba(0,0,0,0.7)',
                borderRadius: '50%',
              }}
              onClick={handleShare}
            >
              <i className='bi bi-share text-white'></i>
            </div>
          </div>

          <div
            className='d-flex align-items-center justify-content-center'
            style={{
              cursor: isLast ? 'not-allowed' : 'pointer',
              padding: '10px',
              width: '50px',
              height: '50px',
              background: isLast ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.7)',
              borderRadius: '50%',
            }}
            onClick={isLast ? undefined : goNext}
          >
            <i className={`fa fa-chevron-right ${isLast ? 'text-muted' : 'text-white'}`}></i>
          </div>
        </div>
      </div>

      {/* Counter di bawah */}
      <div className='mt-3 d-flex justify-content-center w-100'>
        <span className='text-white'>
          Halaman {current} / {total || '-'}
        </span>
      </div>
    </Container>
  )
}
